import AppBar from '@material-ui/core/AppBar';
import Container from '@material-ui/core/Container';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import MenuIcon from '@material-ui/icons/Menu';
import useApiFetch, {apiEndpoint} from '@tracker/shared/src/Auth/Api';
import PreferencesProvider from '@tracker/shared/src/Preferences/PreferencesProvider';
import ProjectsProvider from '@tracker/shared/src/Projects/ProjectsProvider';
import {ClientServiceTeam, Employee} from '@tracker/shared/src/Scheduling/Utils/EmployeeSchedulesCompiler';
import UserMenu from '@tracker/shared/src/UserMenu/UserMenu';
import classnames from 'classnames';
import {useSnackbar} from 'notistack';
import React, {useCallback, useEffect, useState} from 'react';
import FilterDrawer, {drawerWidth} from './components/FilterDrawer';
import Schedule from './components/Schedule';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
    },
    menuButton: {
        marginRight: theme.spacing(2),
    },
    hide: {
        display: 'none',
    },
    title: {
        flexGrow: 1,
    },
    appBar: {
        [theme.breakpoints.up('lg')]: {
            transition: theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        }
    },
    appBarShift: {
        [theme.breakpoints.up('lg')]: {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: drawerWidth,
            transition: theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
    },
    appBarSpacer: theme.mixins.toolbar,
    content: {
        flexGrow: 1,
        padding: theme.spacing(3),
        [theme.breakpoints.up('lg')]: {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: -drawerWidth,
        },
    },
    contentShift: {
        [theme.breakpoints.up('lg')]: {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
        },
    },
}));

const SchedulingContent : React.FC = () => {
    const classes = useStyles();
    const apiFetch = useApiFetch();
    const [employees, setEmployees] = useState<Employee[] | null>(null);
    const [teams, setTeams] = useState<ClientServiceTeam[]>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const [filtersActive, setFiltersActive] = useState(true);

    const loadTeams = useCallback(async () => {
        const response = await apiFetch(new URL('/v1/scheduler/teams/', apiEndpoint).toString());

        if (!response.ok) {
            return enqueueSnackbar('Could not load team list', {variant: 'error'});
        }

        const teams = await response.json() as ClientServiceTeam[];
        setTeams(teams.sort((a, b) => a.abbreviation.localeCompare(b.abbreviation)));
    }, [apiFetch, enqueueSnackbar]);


    const loadEmployees = useCallback(async () => {
        const response = await apiFetch(new URL('/v1/scheduler/employees', apiEndpoint).toString());

        if (!response.ok) {
            return enqueueSnackbar('Could not load employees', {variant: 'error'});
        }

        const employees = await response.json();
        setEmployees(employees);
    }, [apiFetch, enqueueSnackbar]);

    useEffect(() => {
        loadEmployees();
        loadTeams();
    }, [loadEmployees, loadTeams]);

    return (
        <PreferencesProvider categories={['dashboard', 'scheduler']}>
            <div className={classes.root}>
                <AppBar position="fixed" className={classnames(classes.appBar, drawerOpen && classes.appBarShift)}>
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            onClick={() => setDrawerOpen(true)}
                            edge="start"
                            className={classnames(classes.menuButton, drawerOpen && classes.hide)}
                        >
                            <MenuIcon/>
                        </IconButton>
                        <Typography variant="h6" className={classes.title}>
                            Scheduling
                        </Typography>
                        <UserMenu/>
                    </Toolbar>
                </AppBar>

                <FilterDrawer
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    filtersActive={filtersActive}
                    onFiltersActiveChange={setFiltersActive}
                    csTeams={teams}
                />

                <main className={classnames(classes.content, drawerOpen && classes.contentShift)}>
                    <div className={classes.appBarSpacer}/>
                    <Container>
                        <Schedule employees={employees} filtersActive={filtersActive} teams={teams}/>
                    </Container>
                </main>
            </div>
        </PreferencesProvider>
    );
};

const Main : React.FC = () => {
    return (
        <ProjectsProvider>
            <SchedulingContent/>
        </ProjectsProvider>
    );
};

export default Main;
